import { ClientId, Mode, ISharingInformation, ISharingLink, ShareType, ICustomLabeledAudienceConfig, SharingAudience } from '../interfaces/SharingInterfaces';
import QosEvent, { ResultTypeEnum as QosEventEnum } from '@ms/odsp-utilities/lib/logging/events/Qos.event';
import { getLinkLabel, getLinkIconUrl, getVroomScopeFromSharingLinkKind } from '../utilities/ShareHelper';

let PAGE_FINISHED_LOADING = false;

/**
 * Notifies host that "Copy Link" share target was clicked.
 */
export function copyLinkShareTargetClicked() {
    const shareTargetClickedData = {
        name: 'share_copyLinkClicked'
    };

    window.parent.postMessage(JSON.stringify(shareTargetClickedData), '*');
}

/**
 * Notifies host that the dialog was dismissed
 */
export function dismiss() {
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.Dismiss();
    } catch (error) {
        const shareDismissData = {
            name: 'share_dismiss'
        };

        window.parent.postMessage(JSON.stringify(shareDismissData), '*');
    }
}

/**
 * Notifies host that the share action completed
 *
 * @param shareType     the type of share action
 */
export function shareCompleted(shareType: ShareType) {
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.ShareCompleted(shareType);
    } catch (error) {
        const shareCompletedData = {
            name: 'share_completed',
            shareType: shareType
        };
        window.parent.postMessage(JSON.stringify(shareCompletedData), '*');
    }
}

/**
 * Notifies host that the user tapped on Teams
 *
 * @param sharingLink     the sharing link
 */
export function teamsClicked(sharingLink: ISharingLink) {
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.SendLinkViaTeams(sharingLink.url);
    } catch (error) {
        const shareTargetClickedData = {
            name: 'share_teamsClicked'
        };
        window.parent.postMessage(JSON.stringify(shareTargetClickedData), '*');
    }
}

/**
 * Notifies host result of share.
 *
 * @param grantAccessResult boolean Whether or not sharing was successful
 */
export function grantAccessCompleted(grantAccessResult: boolean) {
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.GrantAccessCompleted(grantAccessResult);
    } catch (error) {
        const grantAccessCompletedData = {
            name: 'share_grantAccessCompleted',
            successful: grantAccessResult
        };
        window.parent.postMessage(JSON.stringify(grantAccessCompletedData), '*');
    }
}

/**
 * Notifies host that there was an error.
 *
 * @param sharingInformation object that implements ISharingInformation
 */
export function pageError(shareClickTime: number, clientId: ClientId, mode: Mode, scenarioId: string, sharingInformation: ISharingInformation) {
    firePageLoaded(shareClickTime, clientId, mode, scenarioId, sharingInformation.errorMessage || 'unknown error');
    // Attempt to notify host that there was an error loading the UI.
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.PageError(sharingInformation.error, sharingInformation.errorCategory, sharingInformation.errorMessage, sharingInformation.debugErrorMessage);
    } catch (error) {
        const errorData = {
            name: 'share_error',
            error: sharingInformation.error,
            errorCategory: sharingInformation.errorCategory,
            errorMessage: sharingInformation.errorMessage,
            debugErrorMessage: sharingInformation.debugErrorMessage
        };
        window.parent.postMessage(JSON.stringify(errorData), '*');
    }
}

/**
 * Notifies host that OWA share target was clicked.
 */
export function outlookClicked() {
    const shareTargetClickedData = {
        name: 'share_outlookClicked'
    };

    window.parent.postMessage(JSON.stringify(shareTargetClickedData), '*');
}

/**
 * Notifies host that the UI is rendered and ready to be displayed.
 */
function firePageLoaded(shareClickTime: number, clientId: ClientId, mode: Mode, scenarioId: string, error?: string) {
    /**
     * We only want to log the QoS event once, so check that
     * it already hasn't been fired once.
     */
    if (PAGE_FINISHED_LOADING) {
        return;
    } else {
        PAGE_FINISHED_LOADING = true;
    }

    if (shareClickTime) {
        // Log a QoS event so we know how long it took for page to render.
        /* tslint:disable:no-string-literal */
        const shareFinishedLoadingEvent = new QosEvent({
            name: `ShareV3.${Mode[mode]}.${ClientId[clientId] || 'unknown'}.PageLoaded`,
            startTimeOverride: shareClickTime
        });
        /* tslint:enable:no-string-literal */
        shareFinishedLoadingEvent.end({
            resultType: QosEventEnum.Success,
            extraData: {
                clientId: clientId,
                scenarioId: scenarioId,
                isError: !!error
            }
        });
    }
}

export function pageFinishedLoading(shareClickTime: number, clientId: ClientId, mode: Mode, scenarioId: string) {
    firePageLoaded(shareClickTime, clientId, mode, scenarioId);

    // Notify host
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.PageFinishedLoading();
    } catch (error) {
        const readyData = {
            name: 'share_ready'
        };

        window.parent.postMessage(JSON.stringify(readyData), '*');
    }
}

/**
 * Notifies host that event listener to get default recipients is
 * listening and ready.
 */
export function readyForRecipients() {
    const readyData = {
        name: 'share_readyForRecipients'
    };

    window.parent.postMessage(JSON.stringify(readyData), '*');
}

/**
 * Notifies host that "Send" button was clicked.
 */
export function sendLinkClicked() {
    const sendClickedData = {
        name: 'share_sendClicked'
    };

    window.parent.postMessage(JSON.stringify(sendClickedData), '*');
}

/**
 * Notifies host that the PeoplePicker has gained focus
 */
export function peoplePickerFocused() {
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.PeoplePickerFocused();
    } catch (error) {
        const data = {
            name: 'share_peoplePickerFocused'
        };
        window.parent.postMessage(JSON.stringify(data), '*');
    }
}

/**
 * Notifies host result of link settings changes.
 *
 * @param sharingLink ISharingLink
 * @param companyName string representing the company name
 * @param customLabeledAudienceConfig ICustomLabeledAudienceConfig object
 */
export function linkSettingsCompleted(
    sharingLink: ISharingLink,
    companyName: string,
    customLabeledAudienceConfig: ICustomLabeledAudienceConfig,
    clientId: ClientId) {
    const iconUrl = getLinkIconUrl(sharingLink, customLabeledAudienceConfig);
    const linkLabel = getLinkLabel(sharingLink, companyName, clientId, customLabeledAudienceConfig);
    const scope = getVroomScopeFromSharingLinkKind(sharingLink.sharingLinkKind);
    const shareId = sharingLink.audience === SharingAudience.existing ? '00000000-0000-0000-0000-000000000000' : sharingLink.shareId;
    try {
        const externalJavaScript: any = window.external;
        externalJavaScript.LinkSettingsCompleted(shareId, sharingLink.url, iconUrl, linkLabel, scope);
    } catch (error) {
        const linkSettingsCompletedData = {
            name: 'share_linkSettingsCompleted',
            shareId: shareId,
            linkUrl: sharingLink.url,
            iconUrl: iconUrl,
            linkDescription: linkLabel,
            scope: scope
        };
        window.parent.postMessage(JSON.stringify(linkSettingsCompletedData), '*');
    }
}

export function isDarkMode(): boolean {
    let result = false;
    try {
        const externalJavaScript: any = window.external;
        result = externalJavaScript && externalJavaScript.IsDarkMode && externalJavaScript.IsDarkMode();
    } catch (e) {
        // do nothing
    }
    return result;
}

/**
 * Notifies host that the dialog was dismissed
 */
export function invokeManageAccess() {
    const manageAccessClickedData = {
        name: 'share_manageAccessClicked'
    };

    window.parent.postMessage(JSON.stringify(manageAccessClickedData), '*');
}